import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  input,
  computed,
  ViewChild,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideMapPin, lucidePlus } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmSwitchComponent } from '@spartan-ng/helm/switch';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import {
  BaseCreationDialog,
  BaseDialogConfig,
} from '@shared/components/base-creation-dialog/base-creation-dialog';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { StepLead } from '@shared/components/step-lead/step-lead';
import { FieldShell } from '@shared/components/field-shell/field-shell';
import { SummarySection, SummaryRow } from '@shared/components/summary-section/summary-section';
import { AddressSelector } from '@shared/components/address-selector/address-selector';
import { AddressForm } from '@shared/components/address-form/address-form';
import { AnalysisTypePicker } from '@shared/components/analysis-type-picker/analysis-type-picker';
import { TemplatePicker } from '@shared/components/template-picker/template-picker';
import { AnalysisStore } from '@features/campaigns/store/analysis-store';
import {
  CreateAnalysisRequest,
  AnalysisType,
} from '@shared/models/analysis.model';
import { Address } from '@shared/models/address.model';
import { AddressService } from '@shared/services/address.service';
import { AnalysisService } from '@shared/services/analysis.service';
import { TemplateService } from '@features/catalog/services/template.service';
import { SolarTemplate } from '@features/catalog/models/template.model';
import { ToastService } from '@shared/services/toast.service';
import {
  FIRST_PIPELINE_TASK,
  LAST_PIPELINE_TASK,
} from '@features/analyses/config/pipeline-stage.config';

@Component({
  selector: 'app-create-analysis-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgIconComponent,
    HlmButtonDirective,
    HlmSwitchComponent,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    StepLead,
    FieldShell,
    SummarySection,
    SummaryRow,
    AddressSelector,
    AddressForm,
    AnalysisTypePicker,
    TemplatePicker,
    HlmSeparatorDirective,
    ...BrnDialogImports,
    ...HlmDialogImports,
  ],
  providers: [provideIcons({ lucideMapPin, lucidePlus })],
  templateUrl: './create-analysis-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAnalysisDialog extends BaseCreationDialog implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly analysisStore = inject(AnalysisStore);
  private readonly addressService = inject(AddressService);
  private readonly analysisService = inject(AnalysisService);
  private readonly templateService = inject(TemplateService);
  private readonly toastService = inject(ToastService);

  // Override config input with specific dialog configuration
  override config = input<BaseDialogConfig>({
    title: 'Neue Analyse erstellen',
    description: 'Erstellen Sie eine neue Analyse in drei einfachen Schritten',
    stepLabels: ['Adresse', 'Konfiguration', 'Zusammenfassung'],
    stepLeads: [
      'Suchen Sie die Adresse, für die die Analyse berechnet wird.',
      'Verbrauch und Strompreis bestimmen die Wirtschaftlichkeitsrechnung.',
      'Prüfen Sie die Angaben. Die Analyse läuft anschließend im Hintergrund.',
    ],
    submitButtonText: 'Analyse erstellen',
    eyebrow: 'Analyse',
  });

  // Inputs
  campaignId = input<string | undefined>(undefined);
  organizationId = input.required<string>();

  // Analysis-specific state
  selectedAddress = signal<Address | null>(null);
  showAddressForm = signal(false);

  // Address data owned by this smart component and passed to the presentational AddressSelector
  addresses = signal<Address[]>([]);
  isAddressesLoading = signal(false);
  addressesError = signal<string | null>(null);
  addressTotalCount = signal<number | undefined>(undefined);
  private hasLoadedAddresses = false;
  private addressRequestVersion = 0;

  // ─── "Direkt ausführen" ──────────────────────────────────────────────────────
  // Media cannot be picked here: the selectable files are produced by geocut (task 1),
  // which has not run yet. Same situation as ExecutionPanel.selectProspectiveType —
  // we record the type geocut should produce and let the pipeline pick the file.
  runImmediately = signal(false);
  runAnalysisType = signal<AnalysisType | null>(null);
  runTemplateId = signal<string | null>(null);
  templates = signal<SolarTemplate[]>([]);
  isTemplatesLoading = signal(false);
  templatesError = signal<string | null>(null);
  private hasLoadedTemplates = false;

  /** Both are required before a run can be dispatched — planning (task 5) needs a template. */
  readonly canRunImmediately = computed(
    () => !!this.runAnalysisType() && !!this.runTemplateId(),
  );
  creationSource = computed<'hub' | 'campaign'>(() =>
    this.campaignId() ? 'campaign' : 'hub',
  );

  creationSourceLabel = computed(() =>
    this.creationSource() === 'campaign' ? 'Kampagne' : 'Hub Self-Service',
  );

  /** "Flachdach · Gewerbe" — the building line under the picked address. */
  selectedAddressMeta = computed(() => {
    const a = this.selectedAddress();
    if (!a) return '—';
    const roof =
      a.roofType === 'flat'
        ? 'Flachdach'
        : a.roofType === 'normal'
          ? 'Normaldach'
          : a.roofType === 'both'
            ? 'Gemischt'
            : null;
    const building =
      a.isApartmentBuilding === true
        ? 'Mehrfamilienhaus'
        : a.isApartmentBuilding === false
          ? 'Einfamilienhaus'
          : null;
    const parts = [roof, building].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Nicht angegeben';
  });

  private readonly de = (n: number, decimals = 0) =>
    n.toLocaleString('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  summaryConsumption = computed(
    () =>
      `${this.de(Number(this.analysisForm.get('powerConsumption')?.value ?? 0))} kWh/a`,
  );
  summaryPrice = computed(
    () =>
      `${this.de(Number(this.analysisForm.get('electricityPrice')?.value ?? 0), 4)} €/kWh`,
  );

  /** Reopen the picker so a different address can be chosen. */
  onChangeAddress(): void {
    this.selectedAddress.set(null);
  }

  // Forms for different steps
  analysisForm = this.fb.group({
    powerConsumption: [3500, [Validators.required, Validators.min(1)]],
    electricityPrice: [
      0.3,
      [Validators.required, Validators.min(0.0001), Validators.max(2)],
    ],
  });

  // Step validation
  get canProceedFromStep1(): boolean {
    return this.selectedAddress() !== null;
  }

  get canProceedFromStep2(): boolean {
    return this.analysisForm.valid;
  }

  override canProceedFromCurrentStep(): boolean {
    const step = this.currentStep();
    console.log(
      '[CreateAnalysisDialog] canProceedFromCurrentStep() called. Step:',
      step,
    );
    switch (step) {
      case 1:
        const canStep1 = this.canProceedFromStep1;
        console.log(
          '[CreateAnalysisDialog] Step 1: selectedAddress exists?',
          !!this.selectedAddress(),
          'Can proceed?',
          canStep1,
        );
        return canStep1;
      case 2:
        const canStep2 = this.canProceedFromStep2;
        console.log(
          '[CreateAnalysisDialog] Step 2: form valid?',
          this.analysisForm.valid,
          'Can proceed?',
          canStep2,
        );
        return canStep2;
      case 3:
        const canStep3 =
          this.canProceedFromStep2 &&
          this.selectedAddress() !== null &&
          (!this.runImmediately() || this.canRunImmediately());
        console.log(
          '[CreateAnalysisDialog] Step 3 (final): form valid?',
          this.analysisForm.valid,
          'address selected?',
          !!this.selectedAddress(),
          'Can proceed?',
          canStep3,
        );
        return canStep3;
      default:
        return false;
    }
  }

  ngOnInit(): void {
    // No eager loading — addresses are fetched lazily when the popover first opens.
  }

  /** Called by AddressSelector's (loadRequested) output — lazy-loads addresses on first open. */
  async onLoadAddressesRequested(): Promise<void> {
    if (this.hasLoadedAddresses || this.isAddressesLoading()) return;

    const version = ++this.addressRequestVersion;
    this.isAddressesLoading.set(true);
    this.addressesError.set(null);

    try {
      const result = await this.addressService.getAddressesByOrganization(
        this.organizationId(),
      );
      if (version !== this.addressRequestVersion) return;
      this.addresses.set(result);
      this.addressTotalCount.set(
        this.addressService.getTotalCount(this.organizationId()),
      );
      this.hasLoadedAddresses = true;
    } catch (err) {
      if (version !== this.addressRequestVersion) return;
      this.addressesError.set(
        err instanceof Error ? err.message : 'Fehler beim Laden der Adressen',
      );
      console.error('[CreateAnalysisDialog] Error loading addresses:', err);
    } finally {
      if (version === this.addressRequestVersion) this.isAddressesLoading.set(false);
    }
  }

  /** Called by AddressSelector's (searchChanged) output — the list is capped server-side. */
  async onAddressSearch(term: string): Promise<void> {
    const version = ++this.addressRequestVersion;
    this.isAddressesLoading.set(true);
    this.addressesError.set(null);
    try {
      const result = await this.addressService.findForPicker(this.organizationId(), term);
      if (version !== this.addressRequestVersion) return;
      this.addresses.set(result);
      if (!term.trim()) {
        this.addressTotalCount.set(this.addressService.getTotalCount(this.organizationId()));
        this.hasLoadedAddresses = true;
      }
    } catch (err) {
      if (version !== this.addressRequestVersion) return;
      this.addressesError.set(
        err instanceof Error ? err.message : 'Fehler beim Suchen der Adressen',
      );
    } finally {
      if (version === this.addressRequestVersion) this.isAddressesLoading.set(false);
    }
  }

  /** Templates drive planning (task 5) — only fetched when the run checkbox is ticked. */
  async onRunImmediatelyToggled(checked: boolean): Promise<void> {
    this.runImmediately.set(checked);
    if (!checked || this.hasLoadedTemplates || this.isTemplatesLoading()) return;

    this.isTemplatesLoading.set(true);
    this.templatesError.set(null);
    try {
      this.templates.set(
        await this.templateService.getAll(this.organizationId()),
      );
      this.hasLoadedTemplates = true;
    } catch (err) {
      this.templatesError.set(
        err instanceof Error ? err.message : 'Fehler beim Laden der Vorlagen',
      );
    } finally {
      this.isTemplatesLoading.set(false);
    }
  }

  // Address selection
  onAddressSelected(address: Address): void {
    this.selectedAddress.set(address);
    this.error.set(null);
  }

  onCreateNewAddress(): void {
    this.showAddressForm.set(true);
  }

  onAddressCreated(address: Address): void {
    this.selectedAddress.set(address);
    this.showAddressForm.set(false);
    this.error.set(null);
    // TODO: Refresh address selector to show new address in list
  }

  onAddressFormClosed(): void {
    this.showAddressForm.set(false);
  }

  // Reset dialog state
  protected override resetDialog(): void {
    super.resetDialog();
    this.selectedAddress.set(null);
    this.showAddressForm.set(false);
    this.addresses.set([]);
    this.addressRequestVersion++;
    this.hasLoadedAddresses = false;
    this.addressesError.set(null);
    this.runImmediately.set(false);
    this.runAnalysisType.set(null);
    this.runTemplateId.set(null);
    this.templatesError.set(null);
    this.analysisForm.reset({
      powerConsumption: 3500,
      electricityPrice: 0.3,
    });
  }

  // Handle submission
  protected override async handleSubmit(dialogContext: any): Promise<void> {
    const selectedAddress = this.selectedAddress();
    if (!this.analysisForm.valid || !selectedAddress) {
      throw new Error('Formular ist ungültig oder keine Adresse ausgewählt');
    }

    const formValue = this.analysisForm.value;

    // Validierung der Address-ID (nicht-leer und gültig)
    const addressId = selectedAddress.addressId;
    if (!addressId || addressId.trim() === '') {
      throw new Error('Keine gültige Adresse ausgewählt');
    }

    // Datentyp-Validierung und -Konvertierung
    const powerConsumption = Number(formValue.powerConsumption);
    const electricityPrice = Number(formValue.electricityPrice);

    if (!powerConsumption || powerConsumption <= 0) {
      throw new Error('Stromverbrauch muss eine positive Zahl sein');
    }

    if (!electricityPrice || electricityPrice <= 0) {
      throw new Error('Strompreis muss eine positive Zahl sein');
    }

    const request: CreateAnalysisRequest = {
      addressId: addressId,
      source: this.creationSource(),
      powerConsumption: powerConsumption,
      electricityPrice: electricityPrice,
      // Own the first execute when running immediately, so the automatic geocut
      // does not leave the analysis processing and reject our 1–6 dispatch.
      ...(this.runImmediately() && { skipAutoStart: true }),
...(!this.campaignId() && { organizationId: this.organizationId() }),
    };

    const createdAnalysis = await this.analysisStore.createAnalysis(
      this.organizationId(),
      request,
      this.campaignId(),
    );

    if (!createdAnalysis) {
      throw new Error('Failed to create analysis');
    }

    if (this.runImmediately()) {
      await this.startFullRun(createdAnalysis.analysisId);
    }

    this.submitted.emit(createdAnalysis);
  }

  /**
   * Assign the prospective type + template, then run the whole pipeline in one
   * call — geocut through export. Only reachable when `skipAutoStart` suppressed
   * the usual post-creation geocut, so nothing is in flight here to collide with.
   *
   * Deliberately does not throw: the analysis already exists at this point, so
   * failing the dialog would strand it invisibly. The user is told the creation
   * succeeded and the start did not, and can retry from the analysis itself.
   */
  private async startFullRun(analysisId: string): Promise<void> {
    const analysisType = this.runAnalysisType();
    const templateId = this.runTemplateId();
    if (!analysisType || !templateId) return;

    const orgId = this.organizationId();

    try {
      // Prospective media type: geocut has not produced any file to select yet,
      // so we record the type it should produce — same as ExecutionPanel does
      // for campaign runs that start at geocut.
      await this.analysisService.updateAnalysis(orgId, analysisId, {
        analysisType,
      });
      // selectTemplate persists template_id on the SI analysis; the planning
      // worker reads it off that row when it gets there.
      await this.analysisService.selectTemplate(orgId, analysisId, {
        templateId,
      });
      await this.analysisService.executeAnalysisTasks(orgId, analysisId, {
        start_task: FIRST_PIPELINE_TASK,
        end_task: LAST_PIPELINE_TASK,
      });
      this.toastService.success('Analyse erstellt und vollständig gestartet');
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Unbekannter Fehler';
      this.toastService.error(
        `Analyse erstellt, Start fehlgeschlagen: ${reason}`,
      );
    }
  }
}
